import store from "@/store";
import { createRouter, createWebHistory } from "vue-router";
import { getI18nLocale, setI18nLocale } from "../plugins/i18n";

const routes = [
  {
    path: "/:locale",
    component: () => import("@/views/IndexRouter.vue"),
    children: [
      {
        path: "/:locale",
        name: "Login",
        props: true,
        meta: { public: true },
        component: () => import("@/views/login/Login.vue"),
      },
      {
        path: "/:locale/register",
        name: "Register",
        props: true,
        meta: { public: true },
        component: () => import("@/views/register/Register.vue"),
      },
      {
        path: "/:locale/my-orders",
        name: "OrderList",
        props: true,
        component: () => import("@/views/order-list/OrderList.vue"),
      },
      {
        path: "/:locale/my-organizations",
        name: "OrganizationList",
        props: true,
        component: () => import("@/views/MyOrganizations/OrganizationList.vue"),
      },
      {
        path: "/:locale/my-organizations/detail",
        name: "OrganizationDetail",
        component: () =>
          import("@/views/MyOrganizations/OrganizationDetail.vue"),
      },
      {
        path: "/:locale/verify/:verifyCode?",
        name: "Verify",
        props: true,
        meta: { public: true },
        component: () => import("@/views/verify/Verify.vue"),
        beforeEnter: async (to) => {
          const isLoggedIn = !!localStorage.getItem("auth_token");
          if (!isLoggedIn) {
            const hasTempEmail = !!store.getters["order/tempMail"];
            const hasVerifyCode = !!to.params.verifyCode;
            if (!hasTempEmail && !hasVerifyCode) {
              return { name: "Login", params: { locale: to.params.locale } };
            }
          } else {
            if (store.getters["organization/isAccountVerified"] === null) {
              await store.dispatch("organization/fetchOrganizations");
            }
            const isAccountVerified =
              store.getters["organization/isAccountVerified"];
            const hasTempEmail = !!store.getters["order/tempMail"];
            const hasAccountEmail =
              !!store.getters["organization/accountEmail"];
            const hasVerifyCode = !!to.params.verifyCode;
            if (
              isAccountVerified !== false &&
              !hasTempEmail &&
              !hasAccountEmail &&
              !hasVerifyCode
            ) {
              return {
                name: "OrderList",
                params: { locale: to.params.locale },
              };
            }
          }
        },
      },
      {
        path: "/:locale/verified/:verifyCode",
        name: "Verified",
        props: true,
        component: () => import("@/views/verify/Verify.vue"),
      },
      {
        path: "/:locale/passenger-list",
        name: "ListPassanger",
        props: true,
        component: () => import("@/views/passenger/PassengerList.vue"),
      },
      {
        path: "/:locale/addresses",
        name: "Addresses",
        props: true,
        component: () => import("@/views/address/Addresses.vue"),
      },
      {
        path: "/:locale/auth-modal",
        name: "AuthModel",
        props: true,
        component: () => import("@/views/login-popup/login-popup.vue"),
      },
      {
        path: "/:locale/account-settings",
        name: "AccountSettings",
        props: true,
        component: () =>
          import("@/views/account-settings/Account-Settings.vue"),
      },
      {
        path: "/:locale/passanger-add",
        name: "AddPassenger",
        props: true,
        component: () => import("@/views/passenger/PassengerAddOrUpdate.vue"),
      },
      {
        path: "/:locale/passanger-update/:id",
        name: "UpdatePassenger",
        props: true,
        component: () => import("@/views/passenger/PassengerAddOrUpdate.vue"),
      },
      {
        path: "/:locale/resetPassword/:verifyCode",
        name: "ResetPassword",
        props: true,
        component: () => import("@/views/ResetPassword/ResetPassword.vue"),
      },

      {
        path: "/token",
        name: "Token",
        props: true,
        component: () => import("@/views/ResetPassword/ResetPassword.vue"),
      },

      {
        path: "/:locale/auth",
        name: "Auth",
        props: true,
        component: () => import("@/views/auth/AuthLogin.vue"),
      },
      {
        path: "/:locale/resetPasswordLink/",
        name: "ResetPasswordLink",
        props: true,
        meta: { public: true },
        component: () => import("@/views/ResetPassword/SendPasswordLink.vue"),
      },
      {
        path: "/:locale/resend-verify-code",
        name: "SendVerify",
        props: true,
        meta: { public: true },
        component: () => import("@/views/verify/SendVerify.vue"),
      },
      {
        path: "/:locale/locked",
        name: "LockedPage",
        props: true,
        component: () => import("@/views/LockedPage.vue"),
      },
      {
        path: "/:locale/cancel-order",
        name: "CancelOrder",
        props: true,
        component: () => import("@/views/cancel/CancelOrder.vue"),
      },
      {
        path: "/:locale/order-details",
        name: "OrderDetails",
        props: true,
        meta: { public: true },
        component: () => import("@/views/details/MyDetails.vue"),
      },
      // "Manage booking" links in the confirmation email point at
      // /:locale/orders/:orderId?token=..., while the payment page itself lives at
      // the positional /:locale/:orderId/:token below. Without this record the email
      // URL falls through to "pay" and binds orderId="orders" / token=<order number>,
      // so every self-service call goes to /orders/self-service/orders/... and the
      // API's error response - which carries no CORS headers - surfaces in the
      // browser as a CORS failure (TRAIN-2280). Redirect instead of rendering the
      // view directly, so the rest of the app keeps seeing the canonical "pay" route.
      {
        path: "/:locale/orders/:orderId",
        name: "payFromEmail",
        meta: { public: true },
        redirect: (to) => {
          const { token = "", offerMode, ...query } = to.query;

          // "pay" cannot be built without a token segment, and a link without one
          // carries no session anyway - treat it like any other unknown URL.
          if (!token) {
            return { name: "Login", params: { locale: to.params.locale } };
          }

          return {
            name: "pay",
            params: {
              locale: to.params.locale,
              orderId: to.params.orderId,
              // TrainplanetApi parses offerMode out of the token parameter, so it
              // has to travel inside it - see services/trainplanet.api.js.
              token:
                offerMode === undefined
                  ? token
                  : `${token}&offerMode=${offerMode}`,
            },
            query,
          };
        },
      },
      {
        path: "/:locale/:orderId/:token",
        name: "pay",
        props: true,
        meta: { public: true },
        component: () => import("../views/Checkout/Index.vue"),
      },
      {
        path: "/:locale/:orderId/:token/confirmation",
        name: "confirmation",
        props: true,
        component: () => import("../views/Confirmation/Index.vue"),
      },
      {
        path: "/:locale/unverified-account",
        name: "UnverifiedUserAccountPage",
        props: true,
        component: () => import("../views/UnverifiedUserAccountPage.vue"),
        beforeEnter: (to) => {
          const isLoggedIn = !!localStorage.getItem("auth_token");
          const isVerified = store.getters["organization/isAccountVerified"];
          if (!isLoggedIn) {
            return { name: "Login", params: { locale: to.params.locale } };
          }
          if (isVerified !== false) {
            return { name: "OrderList", params: { locale: to.params.locale } };
          }
        },
      },
      {
        path: "/:locale/:pathMatch(.*)*",
        redirect: { name: "Login" },
      },
    ],
  },
  {
    path: "/:pathMatch(.*)*",
    name: "ErrorNotFound",
    redirect: { name: "Login", params: { locale: "sv" } },
  },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
});

router.beforeEach(async (to, from, next) => {
  try {
    if (window?.location?.href?.includes("forControl")) {
      next();
      return;
    }

    if (!store.state.tenant.theme) {
      await store.dispatch("setTenant");
    }

    let { locale } = to.params;

    if (locale) {
      const localeParameter = locale.toLowerCase();
      let detectedLocale;

      for (const tenantLanguage of store.state.tenant.theme.languages) {
        // "en" => "en-SE"
        // "no" => "nb-NO"
        if (
          tenantLanguage.toLowerCase().startsWith(localeParameter) ||
          tenantLanguage.toLowerCase().endsWith(localeParameter)
        ) {
          detectedLocale = tenantLanguage;
          break;
        }
      }

      const UNVERIFIED_ACCESSIBLE_ROUTES = [
        "Register",
        "Verify",
        "Verified",
        "SendVerify",
        "LockedPage",
        "UnverifiedUserAccountPage",
        "pay",
        "confirmation",
      ];

      const isLoggedIn = !!localStorage.getItem("auth_token");
      const requiresVerification =
        isLoggedIn && !UNVERIFIED_ACCESSIBLE_ROUTES.includes(to.name);

      if (
        requiresVerification &&
        store.getters["organization/isAccountVerified"] === null
      ) {
        await store.dispatch("organization/fetchOrganizations");
      }

      if (
        requiresVerification &&
        store.getters["organization/isAccountVerified"] === false
      ) {
        next({
          name: "UnverifiedUserAccountPage",
          params: { locale: to.params.locale || "sv" },
        });
        return;
      }

      if (!detectedLocale) {
        detectedLocale = store.state.tenant.theme.languages[0];
      }

      if (getI18nLocale() !== detectedLocale) {
        setI18nLocale(detectedLocale);
      }
    }

    next();
  } catch (error) {
    next();
  }
});

// Pages that render without a session declare `meta: { public: true }`. Resolved
// from the live URL rather than router.currentRoute, because currentRoute is still
// START_LOCATION until the first navigation settles - which is exactly when
// App.vue and the AccountApi interceptor need this answer.
export function isPublicRoute(path = window.location.pathname) {
  try {
    return !!router.resolve(path).meta.public;
  } catch (e) {
    // An unresolvable path is not a page we can call public.
    return false;
  }
}

export default router;
